import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { CopyIcon, GlowBorder } from '../../components/ui';
import {
  useReferralEarnings,
  useReferralMembers,
  useReferralSummary,
} from '../../hooks/useApiQuery';
import { formatUsd } from '../../lib/format';
import { CabEmpty, Panel } from '../parts';

export function CabReferrals() {
  const { t, i18n } = useTranslation();
  const summary = useReferralSummary();
  const members = useReferralMembers();
  const earnings = useReferralEarnings();
  const s = summary.data;
  const [copied, setCopied] = useState(false);
  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  const copy = () => {
    if (!s?.ref_link) return;
    void navigator.clipboard?.writeText(s.ref_link).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  // Worked example — friend earns $100, service keeps its fee, your cut
  // is L1% of that fee, the friend keeps the rest. Mirrors the TG page.
  const feePct = Number(s?.platform_default_fee_pct ?? 20);
  const l1Pct = Number(s?.l1_pct ?? 0);
  const exFee = feePct;
  const exCut = (feePct * l1Pct) / 100;
  const exKeep = 100 - feePct;

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="pb-display text-[24px] leading-tight">
          {t('referrals.title')}
        </h1>
        <p className="mt-1 max-w-xl text-[14px] leading-relaxed text-tg-hint">
          {t('referrals.subtitle', { l1: s?.l1_pct ?? '—', l2: s?.l2_pct ?? '—' })}
        </p>
      </div>

      {/* invite link */}
      <GlowBorder radius={20} speedSec={8} innerClassName="relative overflow-hidden p-5 sm:p-6">
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-pb-accent-2/15 blur-3xl" />
        <div className="relative">
          <div className="pb-eyebrow">{t('referrals.your_link')}</div>
          <div className="mt-3 flex items-center gap-2">
            <code className="pb-numeral min-w-0 flex-1 truncate rounded-pb-md border border-pb-border-soft bg-pb-paper2 px-3 py-2.5 text-[13px] text-tg-text">
              {s?.ref_link ?? '…'}
            </code>
            <button
              type="button"
              onClick={copy}
              className="pb-focus-ring inline-flex shrink-0 items-center gap-1.5 rounded-pb-md bg-pb-accent px-3.5 py-2.5 text-[13px] font-medium text-pb-accent-ink hover:bg-pb-accent-hi"
            >
              <CopyIcon size={15} />
              {copied ? t('cabinet.copied') : t('cabinet.copy')}
            </button>
          </div>
          <p className="mt-3 text-[13px] leading-relaxed text-tg-hint">
            {t('referrals.code_body')}
          </p>
        </div>
      </GlowBorder>

      {/* worked example */}
      <Panel title={t('referrals.example_title')}>
        <ul className="space-y-1.5 text-[14px] leading-snug text-tg-text">
          <ExampleLine label={t('referrals.example_friend_earned')} value="+$100.00" />
          <ExampleLine
            label={t('referrals.example_service_fee')}
            value={`−${formatUsd(exFee)}`}
            tone="muted"
          />
          <ExampleLine
            label={t('referrals.example_your_cut')}
            value={`+${formatUsd(exCut)}`}
            tone="positive"
          />
          <li className="my-1 border-t border-pb-border-soft" />
          <ExampleLine label={t('referrals.example_friend_keeps')} value={formatUsd(exKeep)} />
        </ul>
        <p className="mt-3 text-[12px] leading-relaxed text-tg-hint">
          {t('referrals.disclaimer')}
        </p>
      </Panel>

      {/* earnings summary */}
      <div className="grid grid-cols-3 gap-3">
        <Kpi label={t('referrals.earned')} value={formatUsd(s?.total_earned_usd ?? 0)} tone="pos" />
        <Kpi label={t('cabinet.l1_referrals')} value={String(s?.l1_referrals_count ?? 0)} />
        <Kpi label={t('cabinet.l2_referrals')} value={String(s?.l2_referrals_count ?? 0)} />
      </div>

      {/* members */}
      <Panel title={t('referrals.members_heading')}>
        {(members.data?.items ?? []).length ? (
          <div className="divide-y divide-pb-border-soft">
            {members.data!.items.map((m) => {
              const earned = Number(m.earned_from_them_usd);
              return (
                <div key={m.user_id} className="flex items-center gap-3 py-2.5">
                  <span className="pb-display flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pb-accent-soft text-[13px] text-pb-accent">
                    {(m.tg_first_name?.[0] ?? m.tg_username?.[0] ?? '·').toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] text-tg-text">
                      {m.tg_first_name ?? (m.tg_username ? `@${m.tg_username}` : '—')}
                    </div>
                    <div className="pb-caption">
                      L{m.level} · {t('referrals.member_joined', { date: fmtDate(m.joined_at) })}
                    </div>
                  </div>
                  {earned > 0 ? (
                    <span className="pb-numeral text-[13px] text-pb-success">
                      +{formatUsd(earned)}
                    </span>
                  ) : (
                    <span className="pb-caption">{t('referrals.member_earned_zero')}</span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <CabEmpty msg={t('referrals.empty_members_body')} />
        )}
      </Panel>

      {/* earnings */}
      <Panel
        title={t('referrals.earnings_heading')}
        action={
          earnings.data ? (
            <span className="pb-numeral text-[13px] text-pb-success">
              {formatUsd(earnings.data.total_amount_usd)}
            </span>
          ) : null
        }
      >
        {(earnings.data?.items ?? []).length ? (
          <div className="divide-y divide-pb-border-soft">
            {earnings.data!.items.slice(0, 12).map((e) => (
              <div key={e.id} className="flex items-center gap-3 py-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pb-accent-soft text-[14px] text-pb-accent">
                  ✦
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] text-tg-text">
                    {e.source_user_tg_username ? `@${e.source_user_tg_username}` : t('cabinet.kind_ref')}{' '}
                    · L{e.level}
                  </div>
                  <div className="pb-caption">{fmtDate(e.created_at)}</div>
                </div>
                <span className="pb-numeral text-[13px] text-pb-success">
                  +{formatUsd(e.amount_usd)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <CabEmpty msg={t('referrals.empty_earnings_body')} />
        )}
      </Panel>
    </div>
  );
}

function ExampleLine({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'muted' | 'positive';
}) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-tg-hint">{label}</span>
      <span
        className={clsx(
          'pb-numeral font-medium',
          tone === 'positive'
            ? 'text-pb-success'
            : tone === 'muted'
              ? 'text-tg-hint'
              : 'text-tg-text',
        )}
      >
        {value}
      </span>
    </li>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'pos' }) {
  return (
    <div className="rounded-pb-lg border border-pb-border-soft bg-pb-paper/60 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-pb-accent-line hover:shadow-pb-md">
      <div className="pb-caption">{label}</div>
      <div
        className={
          'pb-numeral mt-2 text-[22px] font-semibold leading-none ' +
          (tone === 'pos' ? 'text-pb-success' : 'text-tg-text')
        }
      >
        {value}
      </div>
    </div>
  );
}
